import React from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ShieldCheck,
  TrendingUp,
  Clock,
  Users,
  Star,
  Zap,
  LogIn,
  Sparkles,
  Target,
  BarChart3,
} from "lucide-react";
import logo from "../assets/logo64.png";

const PlanTable = ({ plans }: { plans: any[] }) => (
  <div className="overflow-x-auto rounded-2xl border border-white/10 bg-gradient-to-br from-dark-card/80 to-dark-surface/60 backdrop-blur-xl">
    <table className="w-full text-sm text-left">
      <thead className="text-gray-400 uppercase text-[11px] tracking-wider bg-black/20">
        <tr>
          <th className="px-6 py-4 font-bold">Plan</th>
          <th className="px-6 py-4 font-bold">Price</th>
          <th className="px-6 py-4 font-bold">Signals</th>
          <th className="px-6 py-4 font-bold">Support</th>
          <th className="px-6 py-4 font-bold">Extras</th>
        </tr>
      </thead>
      <tbody className="text-white">
        {plans.map((p, idx) => (
          <tr
            key={p.name}
            className="border-t border-white/5 hover:bg-white/5 transition-all duration-300 group"
          >
            <td className="px-6 py-5 font-bold text-accent-primary group-hover:text-accent-secondary transition-colors">
              {p.name}
            </td>
            <td className="px-6 py-5 font-semibold">{p.price}</td>
            <td className="px-6 py-5 text-gray-300">{p.signals}</td>
            <td className="px-6 py-5 text-gray-300">{p.support}</td>
            <td className="px-6 py-5 text-gray-300">{p.extras}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const LandingPage: React.FC = () => {
  const plans = [
    {
      name: "Starter",
      price: "₹499 / mo",
      signals: "Daily calls",
      support: "Email",
      extras: "Basic analytics",
    },
    {
      name: "Pro",
      price: "₹1,499 / mo",
      signals: "Real-time calls",
      support: "Priority chat",
      extras: "Advanced analytics",
    },
    {
      name: "Institutional",
      price: "Custom",
      signals: "Dedicated feed",
      support: "Account manager",
      extras: "API access",
    },
  ];

  return (
    <div className="min-h-screen bg-[#08090a] text-white antialiased relative overflow-hidden">
      {/* Animated Background Effects */}
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_50%_0%,#1a1b23_0%,transparent_50%)] pointer-events-none" />
      <div className="fixed top-[-10%] left-[-10%] w-[40%] h-[40%] bg-accent-primary/5 rounded-full blur-[120px] pointer-events-none animate-pulse" />
      <div
        className="fixed bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-accent-secondary/5 rounded-full blur-[120px] pointer-events-none animate-pulse"
        style={{ animationDelay: "1s" }}
      />

      {/* Sticky Header */}
      <nav className="sticky top-0 z-50 backdrop-blur-2xl bg-black/40 border-b border-white/5 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-3 group cursor-pointer">
            <div className="relative w-10 h-10">
              <div className="absolute inset-0 bg-accent-primary blur-md opacity-40 group-hover:opacity-100 transition-opacity"></div>
              <img
                src={logo}
                alt="V"
                className="relative w-full h-full object-cover rounded-lg border border-white/20"
              />
            </div>
            <span className="text-2xl font-black tracking-tighter italic bg-gradient-to-r from-white to-orange-500 bg-clip-text text-transparent">
              ytta
            </span>
          </div>

          {/* Nav Actions */}
          <div className="flex items-center gap-4">
            <a
              href="/login"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-accent-primary to-accent-secondary text-white px-5 py-2.5 rounded-full font-bold text-sm shadow-lg shadow-accent-primary/25 hover:shadow-accent-primary/40 hover:scale-105 transition-all"
            >
              <LogIn size={16} />
              <span>Login</span>
            </a>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <header className="max-w-7xl mx-auto px-8 py-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ x: -30, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            {/* Badge */}
            <div className="inline-flex items-center gap-3 mb-6 bg-white/5 border border-white/10 rounded-full px-4 py-2 backdrop-blur-xl">
              <div className="text-xs text-gray-400 font-medium flex items-center gap-1">
                <Sparkles size={14} className="text-accent-primary" />
                Trade smarter. Sleep better.
              </div>
            </div>

            {/* Headline */}
            <h1 className="text-5xl lg:text-6xl font-black leading-[1.1] tracking-tight mb-6">
              <span className="bg-gradient-to-r from-orange-500 via-gray-200 to-gray-400 bg-clip-text text-transparent">
                Actionable market calls
              </span>
              <br />
              <span className="bg-gradient-to-r  from-white via-gray-200 to-orange-500 bg-clip-text text-transparent">
                with clarity
              </span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg text-gray-400 max-w-xl leading-relaxed mb-8">
              Vytta delivers curated trading calls, real-time alerts, and clear
              risk controls. Clean signals, transparent performance, and tools
              that scale from retail traders to institutional desks.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4 items-center mb-12">
              <a
                href="/register"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-white to-orange-500 text-black px-8 py-4 rounded-full font-black text-base shadow-xl hover:shadow-xl transform hover:-translate-y-1 transition-all"
              >
                <LogIn size={20} />
                <span>Register & Get Started</span>
              </a>

              <a
                href="/help" // replace with your phone number in international format
                className="inline-flex items-center gap-2 text-gray-300 px-8 py-4 rounded-full border border-white/10 hover:border-accent-primary/50 hover:text-white transition-all backdrop-blur-xl bg-white/10 shadow-xl"
              >
                <span className="font-semibold">Know More</span>
                <ArrowRight size={18} />
              </a>
            </div>

            {/* Feature Pills */}
            <div className="grid grid-cols-2 gap-4">
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="flex items-center gap-3 bg-gradient-to-br from-green-500/10 to-emerald-500/5 border border-green-500/20 rounded-2xl p-4"
              >
                <div className="w-10 h-10 bg-green-500/20 rounded-xl flex items-center justify-center">
                  <ShieldCheck size={20} className="text-green-400" />
                </div>
                <div>
                  <div className="text-white font-bold text-sm">Risk First</div>
                  <div className="text-gray-400 text-xs">
                    Built-in stop loss
                  </div>
                </div>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.05 }}
                className="flex items-center gap-3 bg-gradient-to-br from-accent-primary/10 to-accent-secondary/5 border border-accent-primary/20 rounded-2xl p-4"
              >
                <div className="w-10 h-10 bg-accent-primary/20 rounded-xl flex items-center justify-center">
                  <TrendingUp size={20} className="text-accent-primary" />
                </div>
                <div>
                  <div className="text-white font-bold text-sm">
                    Performance
                  </div>
                  <div className="text-gray-400 text-xs">Transparent track</div>
                </div>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.05 }}
                className="flex items-center gap-3 bg-gradient-to-br from-yellow-500/10 to-amber-500/5 border border-yellow-500/20 rounded-2xl p-4"
              >
                <div className="w-10 h-10 bg-yellow-500/20 rounded-xl flex items-center justify-center">
                  <Clock size={20} className="text-yellow-400" />
                </div>
                <div>
                  <div className="text-white font-bold text-sm">Real-time</div>
                  <div className="text-gray-400 text-xs">Instant alerts</div>
                </div>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.05 }}
                className="flex items-center gap-3 bg-gradient-to-br from-blue-500/10 to-cyan-500/5 border border-blue-500/20 rounded-2xl p-4"
              >
                <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center">
                  <Users size={20} className="text-blue-400" />
                </div>
                <div>
                  <div className="text-white font-bold text-sm">Community</div>
                  <div className="text-gray-400 text-xs">Trade together</div>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Hero Card */}
          <motion.div
            initial={{ x: 30, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <div className="bg-gradient-to-br from-dark-surface/80 via-dark-card/60 to-dark-surface/40 border border-white/10 rounded-3xl p-8 shadow-2xl backdrop-blur-xl relative overflow-hidden">
              {/* Glow effect */}
              <div className="absolute -top-20 -right-20 w-40 h-40 bg-accent-primary/20 rounded-full blur-3xl" />

              <div className="relative z-10">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <div className="text-xs text-gray-500 uppercase tracking-widest font-bold mb-1">
                      Live Snapshot
                    </div>
                    <div className="text-2xl font-black flex items-center gap-2">
                      NIFTY
                      <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse shadow-[0_0_10px_#10b981]"></span>
                      <span className="text-sm font-normal text-gray-400">
                        1 active
                      </span>
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 bg-white/5 px-3 py-1.5 rounded-full border border-white/10">
                    Updated 2m ago
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-black/20 border border-white/5 rounded-xl p-4">
                      <div className="text-xs text-gray-500 uppercase tracking-wider mb-2 font-bold">
                        Action
                      </div>
                      <div className="text-emerald-400 font-black text-xl flex items-center gap-2">
                        <TrendingUp size={20} />
                        BUY
                      </div>
                    </div>
                    <div className="bg-black/20 border border-white/5 rounded-xl p-4">
                      <div className="text-xs text-gray-500 uppercase tracking-wider mb-2 font-bold">
                        Entry
                      </div>
                      <div className="text-white font-black text-xl">
                        ₹22,450
                      </div>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="relative">
                    <div className="h-3 bg-white/5 rounded-full overflow-hidden border border-white/10">
                      <div className="h-3 bg-gradient-to-r from-accent-primary to-accent-secondary w-2/5 rounded-full" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center gap-2 text-emerald-400">
                      <Target size={16} />
                      <span className="font-bold">Target ₹22,900</span>
                    </div>
                    <div className="flex items-center gap-2 text-rose-400">
                      <ShieldCheck size={16} />
                      <span className="font-bold">SL ₹22,200</span>
                    </div>
                  </div>

                  <div className="mt-6 flex gap-3">
                    <button className="flex-1 bg-gradient-to-r from-accent-primary to-accent-secondary text-black px-4 py-3 rounded-xl font-black hover:scale-105 transition-transform shadow-lg">
                      Follow Call
                    </button>
                    <button className="flex-1 border border-white/10 text-gray-300 px-4 py-3 rounded-xl font-semibold hover:bg-white/5 transition-all">
                      Details
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </header>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-3 gap-6">
          <motion.div
            whileHover={{ y: -8, scale: 1.02 }}
            className="bg-gradient-to-br from-dark-surface/80 to-dark-card/60 border border-white/10 rounded-2xl p-6 backdrop-blur-xl relative overflow-hidden group"
          >
            <div className="absolute -top-10 -right-10 w-32 h-32 bg-accent-primary/10 rounded-full blur-2xl group-hover:bg-accent-primary/20 transition-all" />
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-accent-primary/20 rounded-xl flex items-center justify-center">
                  <Zap size={24} className="text-accent-primary" />
                </div>
                <div className="font-black text-lg">Speed</div>
              </div>
              <p className="text-gray-400 leading-relaxed">
                Lightning-fast alerts with low-latency delivery so you never
                miss an edge.
              </p>
            </div>
          </motion.div>

          <motion.div
            whileHover={{ y: -8, scale: 1.02 }}
            className="bg-gradient-to-br from-dark-surface/80 to-dark-card/60 border border-white/10 rounded-2xl p-6 backdrop-blur-xl relative overflow-hidden group"
          >
            <div className="absolute -top-10 -right-10 w-32 h-32 bg-yellow-500/10 rounded-full blur-2xl group-hover:bg-yellow-500/20 transition-all" />
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-yellow-500/20 rounded-xl flex items-center justify-center">
                  <Star size={24} className="text-yellow-400" />
                </div>
                <div className="font-black text-lg">Clarity</div>
              </div>
              <p className="text-gray-400 leading-relaxed">
                Clear entry, target, and stop loss — no guesswork, just
                execution-ready signals.
              </p>
            </div>
          </motion.div>

          <motion.div
            whileHover={{ y: -8, scale: 1.02 }}
            className="bg-gradient-to-br from-dark-surface/80 to-dark-card/60 border border-white/10 rounded-2xl p-6 backdrop-blur-xl relative overflow-hidden group"
          >
            <div className="absolute -top-10 -right-10 w-32 h-32 bg-blue-500/10 rounded-full blur-2xl group-hover:bg-blue-500/20 transition-all" />
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                  <Users size={24} className="text-blue-400" />
                </div>
                <div className="font-black text-lg">Support</div>
              </div>
              <p className="text-gray-400 leading-relaxed">
                Priority chat and educational content to help you trade smarter
                over time.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <h3 className="text-3xl font-black mb-10 bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
          How Vytta works
        </h3>
        <div className="grid md:grid-cols-3 gap-6">
          <motion.div
            whileHover={{ scale: 1.05 }}
            className="bg-gradient-to-br from-dark-surface/80 to-dark-card/60 border border-white/10 rounded-2xl p-6 backdrop-blur-xl"
          >
            <div className="text-6xl font-black text-accent-primary/20 mb-4">
              1
            </div>
            <div className="text-accent-primary font-black text-lg mb-3">
              Subscribe
            </div>
            <div className="text-gray-400 leading-relaxed">
              Choose a plan and get instant access to the signal feed.
            </div>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.05 }}
            className="bg-gradient-to-br from-dark-surface/80 to-dark-card/60 border border-white/10 rounded-2xl p-6 backdrop-blur-xl"
          >
            <div className="text-6xl font-black text-accent-primary/20 mb-4">
              2
            </div>
            <div className="text-accent-primary font-black text-lg mb-3">
              Receive Alerts
            </div>
            <div className="text-gray-400 leading-relaxed">
              Real-time push and in-app alerts with entry, target, and stop
              loss.
            </div>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.05 }}
            className="bg-gradient-to-br from-dark-surface/80 to-dark-card/60 border border-white/10 rounded-2xl p-6 backdrop-blur-xl"
          >
            <div className="text-6xl font-black text-accent-primary/20 mb-4">
              3
            </div>
            <div className="text-accent-primary font-black text-lg mb-3">
              Execute Confidently
            </div>
            <div className="text-gray-400 leading-relaxed">
              Follow the call, manage risk, and review performance in your
              dashboard.
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <h3 className="text-3xl font-black mb-10 bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
          What traders say
        </h3>
        <div className="grid md:grid-cols-3 gap-6">
          <motion.div
            whileHover={{ y: -8 }}
            className="bg-gradient-to-br from-dark-surface/80 to-dark-card/60 border border-white/10 rounded-2xl p-6 backdrop-blur-xl"
          >
            <div className="flex gap-1 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  size={16}
                  className="fill-yellow-400 text-yellow-400"
                />
              ))}
            </div>
            <div className="text-gray-300 leading-relaxed mb-4">
              "Vytta's calls are crisp and actionable. My win-rate improved and
              I trade with less stress."
            </div>
            <div className="text-sm font-bold text-gray-500">— A. Roy</div>
          </motion.div>

          <motion.div
            whileHover={{ y: -8 }}
            className="bg-gradient-to-br from-dark-surface/80 to-dark-card/60 border border-white/10 rounded-2xl p-6 backdrop-blur-xl"
          >
            <div className="flex gap-1 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  size={16}
                  className="fill-yellow-400 text-yellow-400"
                />
              ))}
            </div>
            <div className="text-gray-300 leading-relaxed mb-4">
              "Real-time alerts and clear SLs made a huge difference. Support is
              responsive too."
            </div>
            <div className="text-sm font-bold text-gray-500">— S. Mehta</div>
          </motion.div>

          <motion.div
            whileHover={{ y: -8 }}
            className="bg-gradient-to-br from-dark-surface/80 to-dark-card/60 border border-white/10 rounded-2xl p-6 backdrop-blur-xl"
          >
            <div className="flex gap-1 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  size={16}
                  className="fill-yellow-400 text-yellow-400"
                />
              ))}
            </div>
            <div className="text-gray-300 leading-relaxed mb-4">
              "Pro plan is worth it for the analytics and API access — saves me
              time and improves execution."
            </div>
            <div className="text-sm font-bold text-gray-500">— R. Banerjee</div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 mt-20 backdrop-blur-xl bg-black/20">
        <div className="max-w-7xl mx-auto px-6 py-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="relative w-10 h-10">
              <div className="absolute inset-0 bg-accent-primary blur-md opacity-40"></div>
              <img
                src={logo}
                alt="V"
                className="relative w-full h-full object-cover rounded-lg border border-white/20"
              />
            </div>
            <div>
              <div className="text-xl font-black tracking-tighter italic bg-gradient-to-r from-white via-accent-primary to-accent-secondary bg-clip-text text-transparent">
                Vytta
              </div>
              <div className="text-gray-500 text-sm">
                © {new Date().getFullYear()} All rights reserved.
              </div>
            </div>
          </div>

          <div className="flex items-center gap-8">
            <a
              href="/login"
              className="text-gray-400 hover:text-white transition-colors font-medium"
            >
              Login
            </a>
            <a
              href="/terms"
              className="text-gray-400 hover:text-white transition-colors font-medium"
            >
              Terms
            </a>
            <a
              href="/privacy"
              className="text-gray-400 hover:text-white transition-colors font-medium"
            >
              Privacy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
